import type { MouseEvent, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Card, Heading, Stack, Text, textColors, textVariants } from '@etg/wings';
import { BrandLogo, Notification, variants } from '@eti/components';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import type { Content, TripType } from '@eti/schema-types';
import { TripCharacteristics } from '@eti/schema-types';
import { css, mediaQueries } from '@eti/styles';
import { constants } from '@eti/utils';
import isFunction from 'lodash/isFunction';
import { QPASS } from '@eti/products';
import ItineraryOverlay from '../../itinerary/ItineraryOverlay';
import ItineraryOverlayTrigger from '../../itinerary/ItineraryOverlayTrigger';
import SeatsLeftInfo from '../../../common/components/SeatsLeftInfo';
import { usePriceDisplayType } from '../../../pages/result/utils/resultUtils';
import ShareTrip from '../../share-trip/ShareTrip';
import type {
  BoundsType,
  TransformedCartTravelerPriceType,
  TransformedIncludedOrUnavailablePersonalItemType,
  TransformedTDCartPaymentMethodPriceType,
  TransformedTDFormContainerSelectedPaymentMethodType,
} from '../../../common/types/transformations/models';
import TripItineraryDrawer from '../../trip-itinerary/TripItineraryDrawer';
import CartTravelerInfo from './traveler-details/components/CartTravelerInfo';
import CartTripInfo from './trip-details/CartTripInfo';
import CartPriceSummary from './CartPriceSummary';
import BaggageSection from './CartBaggageSection';
import { getProductsPerTraveler } from './traveler-details/utils';
import CartQpass from './CartQpass';
import type { CartSelfTransferProps } from './CartSelfTransfer';
import CartSelfTransfer from './CartSelfTransfer';

const { TRIPSTACK } = constants.brands;

const headerSectionStyles = css`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
`;

const providerSystemLogoStyles = css`
  height: 1.25rem;
  width: auto;
`;

const itinerarySectionStyles = css`
  position: relative;
`;

const baggageSectionStyles = css`
  padding-top: 27px;

  @media ${mediaQueries.medium.up} {
    padding-top: 27px;
  }
`;

const itineraryTriggerStyles = css`
  bottom: -17px;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
`;

interface UiCartProps {
  bounds: BoundsType;
  bundleBaggageDetails: Content | null | undefined;
  bundleCabinBaggageDetails: Content | null | undefined;
  bundles: any;
  cabinBaggageSelection: any;
  chargeCurrencyPriceFormat?: any;
  checkedBaggageSelection: any;
  className?: string;
  contactInformation?: ReactNode;
  dataTestId: string;
  discountAmount: number;
  handleSendTrackEvent: () => void;
  instalmentFee?: any;
  isCabinBaggagePresent: boolean | null;
  isCheckedBaggagePresent: boolean | null;
  isMultiStop: boolean;
  isOtherChargeCurrency?: boolean;
  marketingCarrierNames: string;
  numberOfSeatsLeft: number;
  paymentMethods: TransformedTDCartPaymentMethodPriceType[];
  personalItem: TransformedIncludedOrUnavailablePersonalItemType[] | null;
  priceInChargeCurrency?: any;
  priceToDisplay: any;
  products: any[];
  selectedPaymentMethod: TransformedTDFormContainerSelectedPaymentMethodType;
  providerSystems?: string[];
  serviceFee?: {
    currency: {
      code: string;
      exponent: number;
      name: string;
    };
    value: number;
  };
  shareableUrl?: string;
  shouldAddInstallmentFeeInTotalAmount?: boolean;
  shouldShowSeatsLeftNotificationMessage: boolean;
  subTotalPrice: number;
  title?: string;
  totalPrice: number;
  travelerPrices: TransformedCartTravelerPriceType[];
  tripCharacteristics: TripCharacteristics[];
  type: TripType;
  includedExtraProducts?: any;
  chargingCurrency: any;
}

const UiCart = ({
  bounds,
  bundleBaggageDetails,
  bundleCabinBaggageDetails,
  bundles,
  cabinBaggageSelection,
  chargeCurrencyPriceFormat,
  checkedBaggageSelection,
  className,
  contactInformation,
  dataTestId,
  discountAmount,
  handleSendTrackEvent,
  instalmentFee,
  isCabinBaggagePresent,
  isCheckedBaggagePresent,
  isMultiStop,
  isOtherChargeCurrency,
  marketingCarrierNames,
  numberOfSeatsLeft,
  paymentMethods,
  personalItem,
  priceInChargeCurrency,
  priceToDisplay,
  products,
  providerSystems,
  selectedPaymentMethod,
  serviceFee,
  shareableUrl,
  shouldAddInstallmentFeeInTotalAmount,
  shouldShowSeatsLeftNotificationMessage,
  subTotalPrice,
  title,
  totalPrice,
  travelerPrices,
  tripCharacteristics = [],
  type,
  includedExtraProducts,
  chargingCurrency,
}: UiCartProps) => {
  const { t }: { t: (key: any, ...params: any[]) => string } = useTranslation();
  const { p } = useProperty();
  const { agencyAccount } = useSiteContext();
  const [showFlightInfo, setShowFlightInfo] = useState(false);
  const travelers = getProductsPerTraveler(products, travelerPrices);

  const shouldShowItineraryDrawer = p('Result.Page.V3.ItineraryDrawer.Enabled');

  const { filterProducts, isQpassSelected } = useMemo(() => {
    if (products && products.length > 0) {
      const qPassIndex =
        products?.findIndex((product) => {
          return String(product.id) === String(QPASS.id);
        }) ?? -1;

      const hasQpass = qPassIndex !== -1;
      if (hasQpass) {
        products.splice(qPassIndex, 1);
        return { filterProducts: [...products], isQpassSelected: hasQpass };
      }
    }

    return { filterProducts: products, isQpassSelected: false };
  }, [products]);

  const handleCloseFlightInfo = (e: MouseEvent<HTMLButtonElement> | undefined) => {
    if (e) {
      e.stopPropagation();
    }
    setShowFlightInfo(false);
  };
  const handleShowFlightInfo = () => {
    setShowFlightInfo(true);
    if (isFunction(handleSendTrackEvent)) {
      handleSendTrackEvent();
    }
  };

  const isSeatsLeftEnabled = p('Cart.SeatsLeft.Enabled');
  const isSelfTransferGuaranteeEnabledInCart =
    p('SelfTransfer.InformationInCart.Enabled') &&
    tripCharacteristics.includes(TripCharacteristics.VirtualInterlining);

  const selfTransferKeys: CartSelfTransferProps = {
    header: t('Cart.SelfTransfer.Header'),
    label: t('Cart.SelfTransferGuarantee.Label'),
    includedText: t('Cart.SelfTransferGuarantee.Included.Text'),
    link: 'self-transfer',
    productName: 'self-transfer-guarantee',
  };

  const { priceDisplayText } = usePriceDisplayType();

  const qPassKeys = {
    title: t('Cart.Qpass.Header'),
    priceLabel: t('Cart.Qpass.Included.Text'),
  };

  const OWCNotification = tripCharacteristics?.includes(TripCharacteristics.OneWayCombination) ? (
    <Notification dataTestId={`${dataTestId}-owc-notification`} variant={variants.INFORMATION}>
      <Text as="h3" variant={textVariants._16_BOLD}>
        {t('Air.TripInformation.OneWayCombination')}
      </Text>
      <Text asHtml>{t('Air.TripInformation.OneWayCombination.Tooltip')}</Text>
    </Notification>
  ) : null;

  const shouldShowTripStackLogo =
    agencyAccount && providerSystems && providerSystems.includes('Tripstack');

  return (
    <>
      <Card className={className} data-testid={dataTestId}>
        {title && (
          <Card.Section className={shouldShowTripStackLogo ? headerSectionStyles : undefined}>
            <Heading data-testid="cart-title" level={2}>
              {title}
            </Heading>
            {shouldShowTripStackLogo && (
              <Stack align="end" spacing={8}>
                <Text color={textColors.LIGHTER} variant={textVariants._12}>
                  {t('Cart.ProviderSystem.Label')}
                </Text>
                <BrandLogo
                  brand={TRIPSTACK}
                  className={providerSystemLogoStyles}
                  shouldUseContrast
                />
              </Stack>
            )}
          </Card.Section>
        )}
        <Card.Section className={itinerarySectionStyles}>
          <Stack align="end">
            {shareableUrl && (
              <ShareTrip
                bounds={bounds}
                data-testid="shareTrip"
                priceDisplayText={priceDisplayText}
                shareableUrl={shareableUrl}
                totalPrice={priceToDisplay}
                type={type}
              />
            )}
            <CartTripInfo bounds={bounds} isMultiStop={isMultiStop} />
            <ItineraryOverlayTrigger
              className={itineraryTriggerStyles}
              data-testid="itinerary-tripDetails"
              onClick={handleShowFlightInfo}
            />
          </Stack>
        </Card.Section>
        <Card.Section className={baggageSectionStyles}>
          <BaggageSection
            bundleBaggageDetails={bundleBaggageDetails}
            bundleCabinBaggageDetails={bundleCabinBaggageDetails}
            cabinBaggageSelection={cabinBaggageSelection}
            checkedBaggageSelection={checkedBaggageSelection}
            dataTestId={dataTestId}
            isCabinBaggagePresent={isCabinBaggagePresent}
            isCheckedBaggagePresent={isCheckedBaggagePresent}
            personalItem={personalItem}
          />
        </Card.Section>
        {contactInformation}
        <Card.Section>
          <CartTravelerInfo
            bounds={bounds}
            bundles={bundles}
            includedExtraProducts={includedExtraProducts}
            marketingCarrierNames={marketingCarrierNames}
            products={filterProducts}
            serviceFee={serviceFee}
            travelers={travelers}
          />
        </Card.Section>
        {isSelfTransferGuaranteeEnabledInCart && (
          <Card.Section>
            <CartSelfTransfer {...selfTransferKeys} />
          </Card.Section>
        )}
        {isQpassSelected && (
          <Card.Section>
            <CartQpass {...qPassKeys} />
          </Card.Section>
        )}
        <Card.Section>
          <CartPriceSummary
            chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
            chargingCurrency={chargingCurrency}
            dataTestId={`${dataTestId}-priceSummary`}
            discountAmount={discountAmount}
            instalmentFee={instalmentFee}
            isOtherChargeCurrency={isOtherChargeCurrency}
            // @ts-ignore - WEBTECH-1361
            paymentMethods={paymentMethods}
            priceInChargeCurrency={priceInChargeCurrency}
            selectedPaymentMethod={selectedPaymentMethod}
            shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
            subTotalPrice={subTotalPrice}
            totalPrice={totalPrice}
          />
        </Card.Section>
        {isSeatsLeftEnabled && shouldShowSeatsLeftNotificationMessage && (
          <Card.Section>
            <SeatsLeftInfo numberOfSeatsLeft={numberOfSeatsLeft} />
          </Card.Section>
        )}
      </Card>

      {shouldShowItineraryDrawer ? (
        <TripItineraryDrawer
          beforeItineraryContent={OWCNotification}
          bounds={bounds}
          isOpen={showFlightInfo}
          onClose={handleCloseFlightInfo}
          shouldHaveHeaderWithBackButton
          title={t('IbeClient.TripInfo.Header')}
          tripType={type}
        />
      ) : (
        <ItineraryOverlay
          bounds={bounds}
          dataTestId={dataTestId}
          isMultiStop={isMultiStop}
          isOpen={showFlightInfo}
          onClose={handleCloseFlightInfo}
          title={t('IbeClient.TripInfo.Header')}
          topNotification={OWCNotification}
        />
      )}
    </>
  );
};

export default UiCart;
